import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Lock, Mail } from "lucide-react";
import { loginWithCredentials } from "@/lib/auth";

const loginSchema = z.object({
  email: z.string().email("Email invalide"),
  password: z.string().min(8, "Au moins 8 caractères"),
  tenant: z.string().min(1, "Code établissement requis"),
});
type LoginForm = z.infer<typeof loginSchema>;

function LoginPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({ resolver: zodResolver(loginSchema) });

  const onSubmit = async (data: LoginForm) => {
    setError(null);
    setSubmitting(true);
    try {
      await loginWithCredentials(data.email, data.password);
      await navigate({ to: "/" });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md">
        <div className="card-surface rounded-2xl p-8">
          <div className="mb-8 text-center">
            <h1 className="text-3xl font-bold text-foreground">DIKDOC</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Healthcare Operating System
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label htmlFor="tenant" className="mb-1.5 block text-xs uppercase tracking-wide text-muted-foreground">
                Code établissement
              </label>
              <input
                id="tenant"
                type="text"
                autoComplete="organization"
                placeholder="medigroup"
                className="w-full rounded-xl border border-white/10 bg-white/5 px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/60 outline-none transition focus:border-success/60"
                {...register("tenant")}
              />
              {errors.tenant && <p className="mt-1 text-xs text-destructive">{errors.tenant.message}</p>}
            </div>

            <div>
              <label htmlFor="email" className="mb-1.5 block text-xs uppercase tracking-wide text-muted-foreground">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  className="w-full rounded-xl border border-white/10 bg-white/5 py-2.5 pl-9 pr-3.5 text-sm text-foreground placeholder:text-muted-foreground/60 outline-none transition focus:border-success/60"
                  {...register("email")}
                />
              </div>
              {errors.email && <p className="mt-1 text-xs text-destructive">{errors.email.message}</p>}
            </div>

            <div>
              <label htmlFor="password" className="mb-1.5 block text-xs uppercase tracking-wide text-muted-foreground">
                Mot de passe
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  className="w-full rounded-xl border border-white/10 bg-white/5 py-2.5 pl-9 pr-3.5 text-sm text-foreground placeholder:text-muted-foreground/60 outline-none transition focus:border-success/60"
                  {...register("password")}
                />
              </div>
              {errors.password && <p className="mt-1 text-xs text-destructive">{errors.password.message}</p>}
            </div>

            {error && (
              <div className="rounded-xl border border-destructive/30 bg-destructive/10 px-3.5 py-2.5 text-sm text-destructive">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-xl bg-primary py-2.5 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90 disabled:opacity-50"
            >
              {submitting ? "Connexion…" : "Se connecter"}
            </button>
          </form>

          <p className="mt-6 text-center text-xs text-muted-foreground">
            Authentification sécurisée via Keycloak · MFA requis · Conforme HDS / HIPAA
          </p>
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/login/")({
  component: LoginPage,
});
